// components/GoalsSection.tsx
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Target, ChevronDown, ChevronRight, X, Pencil } from 'lucide-react';
import { SectionAccordion } from './SectionAccordion';

export default function GoalsSection({ open, toggle, sectionKey }) {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingText, setEditingText] = useState('');

  useEffect(() => {
    const fetchGoals = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data } = await supabase
        .from('previous_goals')
        .select('*')
        .eq('user_id', session.user.id)
        .order('last_used_at', { ascending: false });

      console.log('Fetched goals:', data);
      setGoals(data || []);
      setLoading(false);
    };

    fetchGoals();
  }, []);

  const handleComplete = async (id) => {
    const completed_at = new Date().toISOString();

    await supabase
      .from('previous_goals')
      .update({ status: 'completed', completed_at })
      .eq('id', id);

    setGoals(prev =>
      prev.map(goal =>
        goal.id === id ? { ...goal, status: 'completed', completed_at } : goal
      )
    );
  };

  const handleDelete = async (id) => {
    await supabase
      .from('previous_goals')
      .delete()
      .eq('id', id);

    setGoals(prev => prev.filter(goal => goal.id !== id));
  };

  const startEditing = (goal) => {
    setEditingGoalId(goal.id);
    setEditingText(goal.text);
  };

  const saveEdit = async () => {
    if (!editingText.trim()) return;

    await supabase
      .from('previous_goals')
      .update({ text: editingText })
      .eq('id', editingGoalId);

    setGoals(prev =>
      prev.map(goal =>
        goal.id === editingGoalId ? { ...goal, text: editingText } : goal
      )
    );
    setEditingGoalId(null);
    setEditingText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
  };

  const active = goals.filter(g => g.status === 'active');
  const completed = goals
    .filter(g => g.status === 'completed')
    .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

  return (
    <SectionAccordion
      title="Goals"
      icon={<Target className="w-5 h-5 text-[#004060]" />}
      open={open}
      toggle={toggle}
      sectionKey={sectionKey}
    >
      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <>
          <div>
            <h3 className="font-semibold text-sm mb-2">Active Goals</h3>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
              {active.map(goal => (
                <div key={goal.id} className="flex items-center justify-between text-sm">
                  <label className="flex items-center space-x-2 flex-grow cursor-pointer">
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => handleComplete(goal.id)}
                      className="form-checkbox h-4 w-4 text-green-600"
                    />

                    {editingGoalId === goal.id ? (
                      <input
                        className="border rounded px-1 py-0.5 w-full text-sm"
                        value={editingText}
                        onChange={e => setEditingText(e.target.value)}
                        onBlur={saveEdit}
                        onKeyDown={handleKeyDown}
                        autoFocus
                      />
                    ) : (
                      <span className="flex-grow">{goal.text || '[No Title]'}</span>
                    )}
                  </label>

                  <div className="flex items-center space-x-2 ml-2">
                    <button onClick={() => startEditing(goal)}>
                      <Pencil className="w-4 h-4 text-gray-500 hover:text-blue-600" />
                    </button>
                    <button onClick={() => handleDelete(goal.id)}>
                      <X className="w-4 h-4 text-gray-500 hover:text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-2 mt-4">
            <button
              className="flex items-center space-x-1 text-sm text-blue-600 font-medium focus:outline-none"
              onClick={() => setShowCompleted(!showCompleted)}
            >
              {showCompleted ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              <span>Completed Goals</span>
            </button>

            {showCompleted && (
              <ul className="space-y-1 text-sm text-gray-600 mt-2 pl-4">
                {completed.map(goal => (
                  <li key={goal.id}>
                    <span className="text-gray-800">{goal.text || '[No Title]'}</span>{' '}
                    <span className="text-xs text-gray-400">
                      (Completed {new Date(goal.completed_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                      })})
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </SectionAccordion>
  );
}
